import { Controller } from "@hotwired/stimulus"
import defaultChart from "../charts/highcharts/default_chart"
import { loadBundle } from "../utils/lazy_bundle"

// Owns the left-table / right-chart layout on /themes and /themes/:id when the
// right panel is a theme index chart.
//
// Arrow-up/down or Space moves the selection between rows. Space also advances.
// Enter navigates to the row's data-theme-href. On selection the controller
// fetches the theme's chart data from the URL template and updates the chart
// in-place without a page reload.
//
// Expected DOM shape:
//   <element data-controller="themes-navigator"
//            data-themes-navigator-chart-url-template-value="/themes/:id/chart">
//     <table>
//       <tr data-themes-navigator-target="row"
//           data-theme-id="1"
//           data-theme-href="/themes/1"
//           data-action="click->themes-navigator#selectByClick">...
//     </table>
//     <div data-themes-navigator-target="chart"></div>
//     <span data-themes-navigator-target="chartTitle"></span>
//   </element>
export default class extends Controller {
  static targets = ["row", "chart", "chartTitle"]
  static values = {
    chartUrlTemplate: String,
    initialId: String,
    initialName: String,
    initialData: Array
  }

  async connect() {
    this.currentRow = null
    this.chart = null
    this.abortController = null

    // Highcharts lives in its own on-demand bundle (see utils/lazy_bundle.js);
    // defaultChart() reads the global, so it has to be here before we build.
    try {
      await loadBundle("highcharts")
    } catch (error) {
      console.error(error)
      return
    }
    if (!this.element.isConnected) return

    this.buildChart()
    this.addKeyboardNavigation()
    this.selectInitialRow()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    if (this.boundKeydownHandler) {
      document.removeEventListener("keydown", this.boundKeydownHandler)
      this.boundKeydownHandler = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  buildChart() {
    if (!this.hasChartTarget) return

    const seriesName = this.hasInitialNameValue ? this.initialNameValue : "Theme"
    const seriesData = this.hasInitialDataValue ? this.initialDataValue : []
    const data = [{ name: seriesName, data: seriesData }]

    this.chart = defaultChart(this.chartTarget, data, {
      xAxisType: "datetime",
      yAxisTitle: "Index Value",
      chartType: "area",
      autoScale: true,
      plotOptions: { area: { fillOpacity: 0.3 } }
    })

    if (this.hasChartTitleTarget) this.chartTitleTarget.textContent = seriesName
  }

  addKeyboardNavigation() {
    this.rowTargets.forEach(row => row.setAttribute("tabindex", "0"))
    this.boundKeydownHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydownHandler)
  }

  selectInitialRow() {
    if (!this.rowTargets.length) return

    let initial = null
    if (this.hasInitialIdValue) {
      initial = this.rowTargets.find(r => r.dataset.themeId === this.initialIdValue)
    }
    this.selectRow(initial || this.rowTargets[0], { skipFetch: true })
  }

  handleKeydown(event) {
    // Don't steal keys from form fields
    const tag = event.target.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target.isContentEditable) return

    const isNav = event.code === "Space" || event.code === "ArrowUp" || event.code === "ArrowDown"
    const isEnter = event.key === "Enter"
    if (!isNav && !isEnter) return

    const rows = this.rowTargets
    if (!rows.length) return

    event.preventDefault()

    if (!this.currentRow) {
      this.selectRow(rows[0])
      return
    }

    if (isEnter) {
      const href = this.currentRow.dataset.themeHref
      if (href) this.navigateTo(href)
      return
    }

    const currentIndex = rows.indexOf(this.currentRow)
    let nextIndex = currentIndex

    if (event.code === "ArrowDown" || event.code === "Space") {
      if (currentIndex < rows.length - 1) nextIndex = currentIndex + 1
    } else if (event.code === "ArrowUp") {
      if (currentIndex > 0) nextIndex = currentIndex - 1
    }

    const nextRow = rows[nextIndex]
    if (nextRow && nextRow !== this.currentRow) {
      this.selectRow(nextRow)
      this.scrollRowIntoView(nextRow)
    }
  }

  selectByClick(event) {
    // `event.currentTarget` is the element carrying the action binding (the row)
    this.selectRow(event.currentTarget)
  }

  selectRow(row, { skipFetch = false } = {}) {
    if (!row) return

    this.rowTargets.forEach(r => r.classList.remove("active"))
    row.classList.add("active")
    this.currentRow = row

    if (!skipFetch) this.loadChart(row)
  }

  async loadChart(row) {
    if (!this.hasChartUrlTemplateValue) return
    const id = row.dataset.themeId
    if (!id) return

    const url = this.chartUrlTemplateValue.replace(":id", encodeURIComponent(id))

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })
      if (!response.ok) return
      const payload = await response.json()
      this.updateChart(payload)
    } catch (error) {
      if (error.name !== "AbortError") console.error("themes-navigator chart load failed:", error)
    }
  }

  updateChart(payload) {
    if (!this.chart || !payload) return

    const series = this.chart.series && this.chart.series[0]
    if (series) {
      series.setData(payload.data || [], false)
      if (payload.name) series.update({ name: payload.name }, false)
    }
    this.chart.redraw()

    if (this.hasChartTitleTarget && payload.name) {
      this.chartTitleTarget.textContent = payload.name
    }
  }

  // Extracted so tests can spy on navigation without touching window.location
  navigateTo(href) {
    window.location.href = href
  }

  scrollRowIntoView(row) {
    const container = this.element.querySelector(".table-responsive") || this.element
    if (!container || !row) return
    const cRect = container.getBoundingClientRect()
    const rRect = row.getBoundingClientRect()
    if (rRect.bottom > cRect.bottom) {
      container.scrollTop += (rRect.bottom - cRect.bottom + 20)
    } else if (rRect.top < cRect.top) {
      container.scrollTop -= (cRect.top - rRect.top + 20)
    }
  }
}
